// Guest portal authentication cookie functions

import { getCookie, setCookie, removeCookie } from './cookieUtils';
import { GuestAuthData } from 'ducks/client/guestPortals';

// Source types for guest portal authentication
export type GuestPortalAuthSource = 'email' | 'qr';

// Enhanced auth data with source information
export interface GuestPortalAuthDataWithSource extends GuestAuthData {
  source: GuestPortalAuthSource;
  created_at: string; // ISO timestamp
}

// Specific functions for guest portal authentication
export const setGuestPortalAuthCookie = (
  authData: GuestAuthData,
  source: GuestPortalAuthSource
): void => {
  // Check if cookie already exists
  const existingCookie = getGuestPortalAuthCookie();
  if (existingCookie) {
    console.log(
      `Guest portal auth cookie already exists (source: ${existingCookie.source || 'unknown'})`
    );
    return;
  }

  // Validate all required fields
  if (!authData) {
    console.warn('No auth data provided for guest portal auth cookie');
    return;
  }

  if (!authData.arrival_date) {
    console.warn('No arrival date provided for guest portal auth cookie');
    return;
  }

  if (!authData.departure_date) {
    console.warn('No departure date provided for guest portal auth cookie');
    return;
  }

  if (!authData.guest_given_name) {
    console.warn('No guest given name provided for guest portal auth cookie');
    return;
  }

  if (!authData.guest_surname) {
    console.warn('No guest surname provided for guest portal auth cookie');
    return;
  }

  // Validate date format and logic
  const arrivalDate = new Date(authData.arrival_date);
  const departureDate = new Date(authData.departure_date);

  if (isNaN(arrivalDate.getTime())) {
    console.warn('Invalid arrival date format for guest portal auth cookie');
    return;
  }

  if (isNaN(departureDate.getTime())) {
    console.warn('Invalid departure date format for guest portal auth cookie');
    return;
  }

  if (arrivalDate >= departureDate) {
    console.warn('Arrival date must be before departure date for guest portal auth cookie');
    return;
  }

  // Calculate days until departure date
  const currentDate = new Date();
  const timeDiff = departureDate.getTime() - currentDate.getTime();
  const daysUntilDeparture = Math.ceil(timeDiff / (1000 * 3600 * 24));

  // Set cookie to expire on departure date (or minimum 1 day)
  const daysToExpire = Math.max(daysUntilDeparture, 1);

  // Create enhanced auth data with source information
  const enhancedAuthData: GuestPortalAuthDataWithSource = {
    ...authData,
    source,
    created_at: new Date().toISOString(),
  };

  const encodedData = btoa(JSON.stringify(enhancedAuthData));
  setCookie('guestPortalAuthData', encodedData, daysToExpire);

  console.log(`Guest portal auth cookie created (source: ${source})`);
};

export const getGuestPortalAuthCookie = (): GuestPortalAuthDataWithSource | null => {
  const cookieData = getCookie('guestPortalAuthData');
  if (!cookieData) return null;

  try {
    const authData = JSON.parse(atob(cookieData));

    // Check if cookie has expired based on departure date
    if (authData.departure_date) {
      const departureDate = new Date(authData.departure_date);
      const currentDate = new Date();

      // Set time to start of day for accurate date comparison
      departureDate.setHours(0, 0, 0, 0);
      currentDate.setHours(0, 0, 0, 0);

      // If current date is after departure date, cookie is expired
      if (currentDate > departureDate) {
        removeCookie('guestPortalAuthData');
        return null;
      }
    }

    return authData;
  } catch (error) {
    removeCookie('guestPortalAuthData');
    return null;
  }
};

export const removeGuestPortalAuthCookie = (): void => {
  removeCookie('guestPortalAuthData');
};
